import React, { useState, useEffect } from "react";
import { useRouteMatch, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import jwtDecode from "jwt-decode";
import store from "../../reduxstore/reduxStore";
import { LoadingAnimation } from "@texada/ui";
import i18n from "i18n/i18n";
import axios from "axios";
import { ReduxState } from "helpers/helper";
import {
  APP_DEFAULT_URL,
  TIS_SESSION_VERIFY_URL
} from "constants/AppConstants";
import { logout } from "../../actions";
import LoginForm from "./LoginForm";
import VerifyLoginUser from "./VerifyLoginUser";
import SRMSyncLoader from "./SRMSyncLoader";
import MobileLogin from "./MobileLogin";
import LoginContext from "./LoginContext";
import LoginInfoDisplay from "./LoginInfoDisplay";
import {
  useLogin,
  switchInstance,
  getLoginError,
  openSrmManagement,
  LoginSource,
  getSelectedLoginTo
} from "./loginUtils";
import MainScreen from "./MainScreen";
import DocDivisionAccessModal from "./DocDivisionAccessModal";

const Login = () => {
  const location = useLocation();
  const { companyId } = useParams();
  const user = useSelector(state => state.auth.user);
  const query = new URLSearchParams(location.search);
  const [isSyncingData, setIsSyncingData] = useState(false);
  const isMobileLogin = !!useRouteMatch("/mobile/:token");
  const isTisVerify = !!useRouteMatch("/verify");
  const { error, login, loginAuth, setError } = useLogin(setIsSyncingData);
  const isLoggedIn = useSelector<ReduxState, boolean[]>(
    state => state.auth.isAuthenticated
  );
  const [isDocDivisionModalOpen, setIsDocDivisionModalOpen] = useState(false);
  const [isValidatingTis, setIsValidatingTis] = useState(false);

  useEffect(() => {
    if (query.get("callbackUrl")) {
      localStorage.setItem("mobileRedirectUrl", query.get("callbackUrl"));
    }
  }, []);

  useEffect(() => {
    if (!isDocDivisionModalOpen && !isMobileLogin && !companyId) {
      if (isLoggedIn) {
        if (query.get("callbackUrl")) {
          store.dispatch(logout());
          return;
        }
        if (
          query.get("applicationId") &&
          getSelectedLoginTo(query.get("applicationId")) === LoginSource.SRM
        ) {
          openSrmManagement(user?.compperms?.operator_by_division);
          return;
        }
        window.location.href = APP_DEFAULT_URL;
        return;
      }
      if (!isTisVerify) {
        setIsValidatingTis(true);
        verifyTisSession();
      }
    }
  }, [isLoggedIn, isDocDivisionModalOpen]);

  useEffect(() => {
    if (!isLoggedIn || !companyId) return;
    onSwitchInstance();
  }, [isLoggedIn, companyId]);

  const onSwitchInstance = async () => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken) {
      window.location.href = APP_DEFAULT_URL;
      return;
    }
    const userData = jwtDecode(jwtToken);

    if (query.get("email") && query.get("email") !== userData.email) {
      store.dispatch(logout());
      window.location.href = `/company/${companyId}?email=${query.get(
        "email"
      )}`;
      return;
    }

    if (userData.tis_session) {
      if (userData.company_upgraded) {
        await switchInstance(
          user.id,
          user?.userinstance?.current.id,
          companyId
        );
      }
      try {
        const redirect = await loginAuth(
          companyId,
          setIsDocDivisionModalOpen,
          undefined,
          userData.company_sso_enabled
        );
        localStorage.setItem("verified", true);
        if (redirect) {
          window.location.href = APP_DEFAULT_URL;
        }
      } catch (e) {
        console.error(e);
        store.dispatch(logout());
        window.location.href = `/company/${companyId}?email=${query.get(
          "email"
        )}`;
        return;
      }
      return;
    }
    window.location.href = `/mobile/${jwtToken}`;
  };

  const verifyTisSession = async () => {
    try {
      const tokenRes = await axios.post(`${TIS_SESSION_VERIFY_URL}/`);
      window.location.href = `${window.location.origin}/verify?tenant=${
        tokenRes.data.user.company
      }&applicationId=${query.get("applicationId") || ""}`;
    } catch (err) {
      setIsValidatingTis(false);
    }
  };

  if (isLoggedIn && companyId) {
    const errorToDisplay = getLoginError(error);
    return (
      <MainScreen>
        {errorToDisplay && <LoginInfoDisplay errorMsg={errorToDisplay} />}
        <SRMSyncLoader message={i18n.t("switching_instance_please_wait")} />
        <DocDivisionAccessModal
          isOpen={isDocDivisionModalOpen}
          onClose={() => {
            setIsDocDivisionModalOpen(false);
            window.location.href = APP_DEFAULT_URL;
          }}
        />
      </MainScreen>
    );
  }

  return (
    <LoginContext.Provider
      value={{
        login,
        error,
        loginAuth,
        setError
      }}
    >
      <div
        className="login-loading"
        style={{ zIndex: isValidatingTis ? 1 : -1 }}
      >
        <LoadingAnimation delay={1000} />
      </div>
      <MainScreen>
        {() => {
          if (isMobileLogin) return <MobileLogin />;
          if (!error && isSyncingData) return <SRMSyncLoader />;
          return isTisVerify ? <VerifyLoginUser /> : <LoginForm />;
        }}
      </MainScreen>
    </LoginContext.Provider>
  );
};

export default Login;
